/* eslint-disable no-param-reassign */
import flagsmith from 'flagsmith';
import _ from 'lodash';
import moment from 'moment';
// eslint-disable-next-line import/no-cycle
import lsOrderService from '@/services/order/order.service';
import lsTosService from '@/services/terms-of-service/terms-of-service.service';
import { CYCLE_MONTHLY, CYCLE_ONE_TIME, CYCLE_YEARLY, INTENT_KEY, serviceType } from '@/constants';
// eslint-disable-next-line import/no-cycle
import lsTrackService from '@/services/ls-track/ls-track.service';
import {
    TURNSTILE_TOKEN,
    generalStorageConstants,
    savedOrderModuleConstants,
} from '@/services/storage/storage-key.constants';
import { lsStorageService } from 'ls-vue-common';
import {
    CART__UPDATE_CART,
    CUSTOMER_LEADS__GET_DETAILS,
    ERROR__CLEAR_ERROR,
    ERROR__SET_ERROR,
    LOADING__FINALIZING_ORDER,
    LOADING__ORDER_ERRORED,
    LOADING__ORDER_QUOTED,
    LOADING__QUOTING_ORDER,
    ORDER_SERVICE_LEAD__GET_DETAILS,
    ORDER__CREATE_QUOTE,
    ORDER__FINALIZE_WITH_PAYMENT,
    ORDER__GENERATE_UUID,
    ORDER__GET_CART_TOTALS,
    ORDER__GET_CYCLE_PRICE_FOR_ONE_TIME_UNAVAILABLE,
    ORDER__GET_DISABLED_DAYS,
    ORDER__GET_DISCOUNT_AMOUNT,
    ORDER__GET_HAS_ONE_TIME_MOWING_OPTION,
    ORDER__GET_INTENT,
    ORDER__GET_ONE_TIME_MOWING_OPTION,
    ORDER__GET_PRICED_LAWNTREATMENT,
    ORDER__GET_PRICED_MOWING_OPTIONS,
    ORDER__MUTATE_EMAIL,
    ORDER__MUTATE_INTENT,
    ORDER__MUTATE_NAME,
    ORDER__MUTATE_ORDER,
    ORDER__MUTATE_PHONE,
    ORDER__MUTATE_PROPERTY_ID,
    ORDER__MUTATE_START_DATE,
    ORDER__UPDATE_EMAIL,
    ORDER__UPDATE_INTENT,
    ORDER__UPDATE_NAME,
    ORDER__UPDATE_ORDER,
    ORDER__UPDATE_PHONE,
    ORDER__UPDATE_PROPERTY_ID,
    ORDER__UPDATE_START_DATE,
} from '../types';

const FEATURE_FLAG_SCALABLE_SIGNUP = 'scalablesignup.abtestcapability';

// Helper Methods
function formatOrder(payload) {
    const order = _.cloneDeep(payload.order);
    const picked = _.pick(payload.address.googlePlace, [
        'city',
        'country',
        'state',
        'street',
        'zip',
    ]);

    picked.street = payload.address.manualStreetNumber
        ? `${payload.address.manualStreetNumber} ${picked.street}`
        : picked.street;
    _.assign(order.address, picked);

    return order;
}

const createInitialState = () => ({
    id: null,
    address: {
        city: '',
        country: '',
        state: '',
        street: '',
        zip: '',
    },
    cart: {
        quotes: [],
        instant_quotes: [],
        services: [],
        total: {
            discount_amount: null,
            service_amount: null,
            tax_amount: null,
            tax_rate: null,
            total_amount: null,
        },
    },
    crtid: null,
    customer_leads_enabled: null,
    deal: null,
    email: '',
    engagement_id: null,
    exposure: null,
    fbcid: null,
    fname: '',
    flags: {},
    gclid: null,
    has_payment: false,
    images: [],
    landingpage: null,
    lead_source: null,
    lname: '',
    name: '',
    notes: '',
    order_id: null,
    order_status: null,
    payment_error: null,
    phone: '',
    policies_accepted_at: null,
    ref: null,
    saved_order: null,
    segment_anonymous_id: null,
    services: {},
    show_faq: true,
    start_date: null,
    status: null,
    trustfee: null,
    updated_at: null,
    url_referrer: null,
    url_signupstart: null,
    utm_campaign: null,
    utm_content: null,
    utm_medium: null,
    utm_source: null,
    utm_term: null,
    zillow: { link: '' },
    zip_in_market: null,
    zip_is_active: null,
    intent: serviceType.MOWING,
});

export const mutations = {
    [ORDER__MUTATE_START_DATE](localState, dateString) {
        localState.start_date = dateString;
    },

    [ORDER__MUTATE_PHONE](localState, { phone }) {
        // strips out any characters not numbers to send to api
        localState.phone = _.replace(`${phone}`, /[^0-9]+/g, '');
    },

    [ORDER__MUTATE_EMAIL](localState, { email }) {
        localState.email = email;
    },

    [ORDER__MUTATE_PROPERTY_ID](localState, { propertyId }) {
        localState.property_id = propertyId;
    },

    [ORDER__MUTATE_NAME](localState, { name, fname, lname }) {
        if (fname && _.isUndefined(lname)) {
            localState.fname = fname;
            localState.name = `${fname} ${localState.lname}`;
        } else if (lname && _.isUndefined(fname)) {
            localState.lname = lname;
            localState.name = `${localState.fname} ${lname}`;
        } else if (fname && lname) {
            localState.fname = fname;
            localState.lname = lname;
            localState.name = `${fname} ${lname}`;
        } else if (name) {
            const splitName = _.split(name, ' ');
            localState.name = name;
            localState.fname = _.head(splitName) || '';
            localState.lname = _.tail(splitName).join(' ') || '';
        }
    },

    [ORDER__MUTATE_ORDER](localState, payload) {
        _.assign(localState, payload);
    },

    [ORDER__MUTATE_INTENT](localState, intent) {
        localState.intent = intent;
    },
};

function dispatchLeadDetailsAsync(dispatch, response) {
    dispatch(ORDER__UPDATE_ORDER, response.data);
    dispatch(LOADING__ORDER_QUOTED, response);

    const targetStore = response.data.customer_leads_enabled
        ? CUSTOMER_LEADS__GET_DETAILS
        : ORDER_SERVICE_LEAD__GET_DETAILS;

    dispatch(targetStore, { id: response.data.id });
}

function dispatchLeadDetailsSynchronous(dispatch, response) {
    const targetStore = response.data.customer_leads_enabled
        ? CUSTOMER_LEADS__GET_DETAILS
        : ORDER_SERVICE_LEAD__GET_DETAILS;

    dispatch(targetStore, { id: response.data.id }).finally(() => {
        dispatch(ORDER__UPDATE_ORDER, response.data);
        dispatch(LOADING__ORDER_QUOTED, response);
    });
}

function dispatchLeadDetails(dispatch, response) {
    const zipInMarket = _.get(response, 'data.zip_in_market');
    const zipIsActive = _.get(response, 'data.zip_is_active');

    if (zipInMarket && zipIsActive) {
        dispatchLeadDetailsAsync(dispatch, response);
        return;
    }

    // If order is out of market, leads must be loaded
    // to render the screen /single-signup/form/out-of-market
    dispatchLeadDetailsSynchronous(dispatch, response);
}

const handleSignupWebRedirect = (formattedOrder) => {
    const {
        address: { street, city, state: targetState, zip, country },
    } = formattedOrder;

    const formattedAddress = `${street}, ${city}, ${targetState}, ${zip}, ${country}`;
    const queryParams = new URLSearchParams();
    queryParams.append('name', formattedOrder.name);
    queryParams.append('phone', formattedOrder.phone);
    queryParams.append('address', formattedAddress);
    const availableParams = {
        deal: 'deal',
        segid: 'segment_anonymous_id',
        intent: 'intent',
        utm_source: 'utm_source',
        utm_medium: 'utm_medium',
        utm_term: 'utm_term',
        utm_content: 'utm_content',
        utm_campaign: 'utm_campaign',
        gclid: 'gclid',
        fbcid: 'fbcid',
    };

    Object.keys(availableParams).forEach((param) => {
        const orderParam = availableParams[param];
        if (!formattedOrder[orderParam]) {
            return;
        }

        queryParams.append(param, formattedOrder[orderParam]);
    });

    const url = `
        ${process.env.VUE_APP_SIGNUP_WEB_URL}/cart/contact-info?${queryParams.toString()}
    `;

    window.location.href = url;
};

const isSignupWebReferral = () => document.referrer.match(new RegExp(/signup-web/));

const isTreatment = () => sessionStorage.getItem(INTENT_KEY) === serviceType.TREATMENT;

const handleLegacyOrderCreation = (
    formattedOrder,
    changingAddress,
    currentEmail,
    type,
    dispatch
) => {
    if (changingAddress) {
        if (isSignupWebReferral() && !isTreatment()) {
            handleSignupWebRedirect(formattedOrder);
            return;
        }

        // preserve current user email and reset it on order after request to avoid duplicate
        // email error flow issues, only do this when changingAddress is passed in from modal
        // as it causes weird user flow issues
        currentEmail = formattedOrder.email;
        delete formattedOrder.email;
    }

    const orderId = formattedOrder.order_id;

    formattedOrder.attach_default_service = true;
    formattedOrder.type = type || serviceType.MOWING;
    formattedOrder.turnstileToken = lsStorageService.get(TURNSTILE_TOKEN);
    lsOrderService
        .createOrUpdateOrder(formattedOrder)
        .then((response) => {
            lsStorageService.set('ls.order_created', true);
            if (changingAddress && currentEmail) {
                _.assign(response.data, { email: currentEmail });
            }

            // If it is a new order, generate uuid.
            if (!orderId) {
                dispatch(ORDER__GENERATE_UUID, response);
            }

            dispatchLeadDetails(dispatch, response);
        })
        .catch((err) => {
            dispatch(ERROR__SET_ERROR, err);
            dispatch(LOADING__ORDER_ERRORED);
        });
};

export const actions = {
    [ORDER__UPDATE_START_DATE]({ commit }, dateString) {
        commit(ORDER__MUTATE_START_DATE, dateString);
    },

    [ORDER__UPDATE_PHONE]({ commit, state }, payload) {
        commit(ORDER__MUTATE_PHONE, payload);
        if (!payload.invalid) {
            lsStorageService.set(savedOrderModuleConstants.PHONE, state.phone);
        }
    },

    [ORDER__UPDATE_NAME]({ commit, state }, payload) {
        commit(ORDER__MUTATE_NAME, payload);
        if (!payload.invalid) {
            if (state.name) {
                lsStorageService.set(savedOrderModuleConstants.NAME, state.name.trim());
            }
            if (state.fname) {
                lsStorageService.set(savedOrderModuleConstants.FNAME, state.fname.trim());
            }
            if (state.lname) {
                lsStorageService.set(savedOrderModuleConstants.LNAME, state.lname.trim());
            }
        }
    },

    [ORDER__UPDATE_EMAIL]({ commit, state }, payload) {
        commit(ORDER__MUTATE_EMAIL, payload);
        if (!payload.invalid) {
            lsStorageService.set(savedOrderModuleConstants.EMAIL, state.email);
        }
    },

    [ORDER__UPDATE_PROPERTY_ID]({ commit, state }, payload) {
        commit(ORDER__MUTATE_PROPERTY_ID, payload);
        if (!payload.invalid) {
            lsStorageService.set(savedOrderModuleConstants.PROPERTY_ID, state.propertyId);
        }
    },

    [ORDER__UPDATE_ORDER]({ commit, dispatch, state }, payload) {
        commit(ORDER__MUTATE_ORDER, payload);
        lsStorageService.set(savedOrderModuleConstants.ADDRESS, state.address);
        lsStorageService.set(savedOrderModuleConstants.DEAL, state.deal);
        lsStorageService.set(savedOrderModuleConstants.SAVED_ORDER, state.saved_order);
        lsStorageService.set(savedOrderModuleConstants.LEAD_SOURCE, state.lead_source);
        // keep cart synced with source of truth
        dispatch(CART__UPDATE_CART, state.cart);
    },

    [ORDER__CREATE_QUOTE](
        { state, dispatch, rootState },
        { address = null, changingAddress = false, type = null }
    ) {
        let currentEmail;
        dispatch(ERROR__CLEAR_ERROR);
        dispatch(LOADING__QUOTING_ORDER);
        const targetAddress = !address ? rootState.address : address;
        const formattedOrder = formatOrder({ order: state, address: targetAddress });

        /**
         * Customer is not looking for mowing
         */
        if (isSignupWebReferral()) {
            handleLegacyOrderCreation(
                formattedOrder,
                changingAddress,
                currentEmail,
                type,
                dispatch
            );

            return;
        }

        /**
         * If customer reached the second page
         * and tries to reload it, the page
         * can restart flagsmith status
         */
        if (!flagsmith.initialised) {
            console.log("flagsmith hasn't been initialised");
            handleLegacyOrderCreation(
                formattedOrder,
                changingAddress,
                currentEmail,
                type,
                dispatch
            );

            return;
        }

        const { phone } = formattedOrder;
        const formattedPhone = phone.replaceAll('-', '');
        const userId = `${formattedPhone}_${process.env.VUE_APP_BRAND}`;
        flagsmith
            .identify(userId, { phoneNumber: formattedPhone })
            .then(() => {
                const flag = flagsmith.getAllFlags()[FEATURE_FLAG_SCALABLE_SIGNUP];
                if (!flag.enabled || !flag.value || isTreatment()) {
                    lsTrackService.track({
                        data: {
                            experiment_name: FEATURE_FLAG_SCALABLE_SIGNUP,
                            experiment_variation: 'control',
                            experiment_flag: flag,
                        },
                        type: 'Experiment Viewed',
                    });

                    handleLegacyOrderCreation(
                        formattedOrder,
                        changingAddress,
                        currentEmail,
                        type,
                        dispatch
                    );

                    return;
                }

                lsTrackService.track({
                    data: {
                        experiment_name: FEATURE_FLAG_SCALABLE_SIGNUP,
                        experiment_variation: 'experiment',
                        experiment_flag: flag,
                    },
                    type: 'Experiment Viewed',
                });

                handleSignupWebRedirect(formattedOrder);
            })
            .catch(() =>
                handleLegacyOrderCreation(
                    formattedOrder,
                    changingAddress,
                    currentEmail,
                    type,
                    dispatch
                )
            );
    },

    [ORDER__FINALIZE_WITH_PAYMENT]({ state, dispatch }, { error, token }) {
        dispatch(ERROR__CLEAR_ERROR);
        dispatch(LOADING__FINALIZING_ORDER, { status: true });
        const onError = (err) => {
            dispatch(ERROR__SET_ERROR, err);
            dispatch(LOADING__FINALIZING_ORDER, { status: false });
        };

        if (error) {
            const errorMsg = error.message || 'Invalid credit card information.';
            onError(new Error(errorMsg));
            return;
        }

        const onSuccess = ({ authtoken, redirectTimeout }) => {
            setTimeout(() => {
                // redirect with slight delay to ensure final track calls
                window.location.href = `${process.env.VUE_APP_LEGACY_CUSTOMER_BASE_URL}?auth=${authtoken}`;
            }, redirectTimeout);
        };

        const orderWithPaymentInfo = _.assign({ payment_information: token.id }, state);
        lsOrderService
            .update(orderWithPaymentInfo)
            .then((response) => {
                const finalOrder = response.data;
                if (finalOrder.payment_error) {
                    throw finalOrder.payment_error;
                }

                if (finalOrder.has_payment) {
                    const customerId = _.get(finalOrder, 'customer.id');
                    const authtoken = _.get(finalOrder, 'customer.user.authtoken.authtoken_id');
                    const redirectTimeout = 300;

                    // success set auth token, send track event, redirect to my.
                    lsStorageService.set(generalStorageConstants.AUTH, authtoken);
                    lsTrackService.completeCheckout(finalOrder);
                    lsOrderService.clearStoredOrderData();

                    // if we are in RN WebView - return authtoken
                    if (window.isLSCustAppWebView) {
                        // if customer app is >= v4.5.2, they will hit this first one
                        if (_.get(window, 'ReactNativeWebView.postMessage')) {
                            window.ReactNativeWebView.postMessage(
                                JSON.stringify({ authtoken, status: 'signupComplete' })
                            );
                        } else {
                            window.postMessage(
                                JSON.stringify({ authtoken, status: 'signupComplete' })
                            );
                        }
                    }

                    lsTosService
                        .acceptToS(customerId)
                        .then(() => {
                            onSuccess({ authtoken, redirectTimeout });
                        })
                        .catch((err) => {
                            if (_.get(err, 'message') === lsTosService.constants.ALREADY_ACCEPTED) {
                                onSuccess({ authtoken, redirectTimeout });
                                return;
                            }

                            onError(err);
                        });
                }
            })
            .catch(onError);
    },

    [ORDER__GENERATE_UUID](localState, createdOrder) {
        lsTrackService.recordAnonymousIdToOrder(createdOrder);
    },

    [ORDER__UPDATE_INTENT]({ commit }, intent) {
        commit(ORDER__MUTATE_INTENT, intent);
    },
};

export const getters = {
    [ORDER__GET_PRICED_LAWNTREATMENT](localState) {
        const fertilization = _.get(localState, 'services.fertilization');
        const service = _.get(fertilization, 'service');
        const price = _.get(fertilization, 'options[0]');

        return price && service ? { price, service } : false;
    },

    [ORDER__GET_PRICED_MOWING_OPTIONS](localState) {
        const market = _.get(localState, 'market');
        const mowing = _.get(localState, 'services.mowing');
        const mowingPriceOptions = _.get(mowing, 'options');
        const isMonthlyMowingDisabled =
            market?.mowing_monthly_disabled_starts_at &&
            market?.mowing_monthly_disabled_ends_at &&
            moment().isBetween(
                market.mowing_monthly_disabled_starts_at,
                market.mowing_monthly_disabled_ends_at
            );

        let mowingOptions = _.map(mowingPriceOptions, (priceOption) => ({
            price: priceOption,
            service: mowing.service,
        }));

        // If monthly mowing is disabled, hide the price
        if (isMonthlyMowingDisabled) {
            mowingOptions = mowingOptions.filter((option) => option.price.cycle !== CYCLE_MONTHLY);
        }

        // if cycle is 0 it is a one time mow pricing and we want it to be at the end
        // of the array so return an abnormally high value, 365 chosen arbitrarily
        mowingOptions = _.sortBy(mowingOptions, (option) => {
            if (option.price.cycle === CYCLE_ONE_TIME) {
                return CYCLE_YEARLY;
            }

            return option.price.cycle;
        });

        return _.size(mowingOptions) ? mowingOptions : false;
    },

    [ORDER__GET_HAS_ONE_TIME_MOWING_OPTION](localState, otherGetters) {
        const mowingOptions = otherGetters[ORDER__GET_PRICED_MOWING_OPTIONS];
        const lastMowingOption = _.last(mowingOptions);

        return _.get(lastMowingOption, 'price.cycle') === 0;
    },

    [ORDER__GET_ONE_TIME_MOWING_OPTION](localState, otherGetters) {
        const mowingOptions = otherGetters[ORDER__GET_PRICED_MOWING_OPTIONS];

        const unpricedOneTime = {
            price: {
                price_id: null,
                cycle: 0,
            },
            service: {
                name: 'Lawn Mowing',
                service_id: 1,
            },
        };

        return _.find(mowingOptions, { price: { cycle: 0 } }) || unpricedOneTime;
    },

    [ORDER__GET_CYCLE_PRICE_FOR_ONE_TIME_UNAVAILABLE](localState, otherGetters) {
        const mowingOptions = otherGetters[ORDER__GET_PRICED_MOWING_OPTIONS];

        return (
            _.find(mowingOptions, { price: { cycle: 14 } }) ||
            _.find(mowingOptions, { price: { cycle: 7 } }) ||
            _.find(mowingOptions, { price: { cycle: 28 } }) ||
            {}
        );
    },

    [ORDER__GET_DISABLED_DAYS]({ sellable = {} }) {
        const disabledDays = [];

        if (sellable.monday !== true) {
            disabledDays.push(1);
        }
        if (sellable.tuesday !== true) {
            disabledDays.push(2);
        }
        if (sellable.wednesday !== true) {
            disabledDays.push(3);
        }
        if (sellable.thursday !== true) {
            disabledDays.push(4);
        }
        if (sellable.friday !== true) {
            disabledDays.push(5);
        }
        if (sellable.saturday !== true) {
            disabledDays.push(6);
        }
        if (sellable.sunday !== true) {
            disabledDays.push(0);
        }

        return disabledDays;
    },

    [ORDER__GET_CART_TOTALS]({ cart }) {
        return cart.total;
    },

    [ORDER__GET_DISCOUNT_AMOUNT]({ cart }) {
        return cart.total.discount_amount;
    },

    [ORDER__GET_INTENT]({ intent }) {
        return intent;
    },
};

export const order = {
    state: createInitialState,
    mutations,
    actions,
    getters,
};
